import sharedConfig from "../config";
import apps from "../apps";
import { loginSession } from "../LoginSession";

/**
 * Get the relative part of a url object or string url.
 * @param {URL | string} url
 * @returns {string} The relative path of the URL.
 * @throws {TypeError} If the url is not a valid URL object and can't be converted to one with URL(url).
 */
export function getRelativePath(url) {
    if (url instanceof URL === false) url = new URL(url); // does error throwing for us
    return url.pathname + url.search + url.hash;
}

/**
 * get a landing page based on superuser status + optional app specificity.
 */
export function getPrimaryLandingPage(userInfo = loginSession.userInfo) {
    const expeditedApp = apps[userInfo?.single_app_ux];
    return expeditedApp
        ? // if marked for single app experience, use the apps landing page as the primary
          expeditedApp.getAppLandingPage(userInfo)
        : // if no app is marked for single app experience, use central hub
          sharedConfig.centralFrontendURL + (userInfo?.superuser ? "superuser" : "hub");
}

/**
 * Formats a `Date` object.
 * note: If date is not a `Date` object, it will be converted to one before formatting.
 * @param {Date | string} date The date to format.
 * @returns {string} The date formatted as `mmmm dd, yyyy`. or an empty string if the date is falsy.
 * @throws {TypeError} If date is not a `Date` and is not a valid argument for the `Date` constructor.
 */
export function formatDate(date) {
    //empty string if date not provided
    if (!date) {
        return "";
    }

    // if its not a date try to make it one.
    date = date instanceof Date ? date : new Date(date);

    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

/**
 * Formats the date in a terse but human format, with 3 char month names.
 * note: If date is not a `Date` object, it will be converted to one before formatting.
 * @param {Date | string} date The date to format.
 * @returns {string} The date formatted as `mmmm dd, yyyy`. or an empty string if the date is falsy.
 * @throws {TypeError} If date is not a `Date` and is not a valid argument for the `Date` constructor.
 */
export function formatDateTerse(date) {
    //empty string if date not provided
    if (!date) {
        return "";
    }

    // if its not a date try to make it one.
    date = date instanceof Date ? date : new Date(date);

    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

/**
 * Converts a total time in seconds to a human-readable string in minutes and seconds.
 *
 * @param {number} totalTimeInSeconds The total time in seconds.
 * @returns {string | null} A formatted string representing the time in minutes and seconds.
 * Returns `null` if `totalTimeInSeconds` is less than 0 or falsy.
 *
 * @example
 * secondsToMinutesAndSeconds(45); // "45 seconds"
 * secondsToMinutesAndSeconds(75); // "1 minute and 15 seconds"
 * secondsToMinutesAndSeconds(120); // "2 minutes"
 */
export function secondsToMinutesAndSeconds(totalTimeInSeconds) {
    if (!totalTimeInSeconds || totalTimeInSeconds < 0) {
        return null;
    }

    if (totalTimeInSeconds < 60) {
        return `${totalTimeInSeconds} seconds`;
    }

    const minutes = Math.floor(totalTimeInSeconds / 60);
    const seconds = totalTimeInSeconds % 60;

    let result = minutes === 1 ? `${minutes} minute` : `${minutes} minutes`;
    if (seconds > 0) {
        result += ` and ${seconds} seconds`;
    }

    return result;
}

/**
 * Downloads a file to the user's local file system.
 *
 * @note If you want to download a ZIP and the data comes from an HTTP request,
 * you must set the response type to `blob`.
 *
 * @example // Downloading a ZIP using axios
 * const response = axios.get("/example-route", {
 *  responseType: "blob",
 *  ...
 * });
 *
 * @param {any} data The data for the file.
 * @param {string} filename The name of the file.
 * @param {string} mimeType The MIME type of the file. Defaults to "application/octetstream" for backwards compatibility.
 */
export function downloadFile(data, filename, mimeType = "application/octetstream") {
    // Convert the Byte Data to BLOB object.
    const blob = new Blob([data], { type: mimeType });

    // Check the Browser type and download the File.
    const isIE = false || !!document.documentMode;
    if (isIE) {
        window.navigator.msSaveBlob(blob, filename);
    } else {
        const url = window.URL || window.webkitURL;
        const link = url.createObjectURL(blob);

        const a = document.createElement("a");
        document.body.appendChild(a);
        a.style = "display: none";
        a.href = link;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(link);
        a.remove();
    }
}

/**
 * Creates a debounced function that delays invoking `func` until after `waitMs` milliseconds
 * have elapsed since the last time the debounced function was invoked.
 *
 * if `waitMs` <= 0, the same function will be returned unmodified.
 *
 * @param {Function} func - The function to debounce.
 * @param {number} waitMs - The number of milliseconds to delay.
 * @returns {Function} A new debounced function.
 */
export function debounce(func, waitMs) {
    if (waitMs <= 0) return func; // No debounce needed

    let timeout;

    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };

        clearTimeout(timeout);
        timeout = setTimeout(later, waitMs);
    };
}

export class FileSize {
    /**
     * Object that converts bytes to a human-readable file size unit.
     *
     * For example, if the number of bytes is at least one KB, then the size is converted
     * to KB, and so on.
     *
     * @param {number} sizeBytes Size of the file in bytes.
     */
    constructor(sizeBytes) {
        if (isNaN(sizeBytes) || sizeBytes < 0) {
            throw new Error("Invalid file size input. Size must be a non-negative number.");
        }

        const { size, unit } = this.#convertBytes(sizeBytes);

        this.size = size;
        this.unit = unit;
    }

    /**
     * Converts bytes to a human-readable file size unit.
     * @param {number} bytes Size in bytes to convert.
     * @returns An object containing the size and unit.
     */
    #convertBytes(bytes) {
        let size = 0;
        let unit = "";

        if (bytes < 1024) {
            size = bytes;
            unit = "bytes";
        } else if (bytes < Math.pow(1024, 2)) {
            size = (bytes / 1024).toFixed(2);
            unit = "KB";
        } else if (bytes < Math.pow(1024, 3)) {
            size = (bytes / Math.pow(1024, 2)).toFixed(2);
            unit = "MB";
        } else {
            size = (bytes / Math.pow(1024, 3)).toFixed(2);
            unit = "GB";
        }

        return { size, unit };
    }
}

/**
 * Checks if the request configuration is for an authentication-related route under /api/auth,
 * which should not be subject to token refreshing interception.
 *
 * @param {Object} requestConfig - The configuration object for the request.
 * @returns {boolean} True if the request is directed to an auth route on the central backend, false otherwise.
 */
export function isMetaAuthRequest(requestConfig) {
    // Normalize URL regardless of path/baseURL format
    const requestUrl = new URL(requestConfig.url, requestConfig.baseURL);

    // Check if the request is to the central backend and starts with the auth base path
    return (
        requestUrl.host === new URL(sharedConfig.centralBackendURL).host && requestUrl.pathname.startsWith("/api/auth")
    );
}

// IMPORTANT: This function only works on frontends (it uses web-only APIs)
export async function copyToClipboard(textToCopy) {
    if (navigator.clipboard) {
        await navigator.clipboard.writeText(textToCopy);
    } else {
        // Fallback for the HTTP protocol and browsers that don't support the clipboard API
        const tempInput = document.createElement("input");
        tempInput.value = textToCopy;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand("copy");
        document.body.removeChild(tempInput);
    }
}

/**
 * Capitalizes the first letter of a string.
 * @param {string} [str] The string to capitalize the first letter of.
 * @returns {string} The string with the first letter capitalized. If the input is null or undefined, it returns the string as is.
 */
export function capitalizeFirstLetter(str) {
    if (str === null || str === undefined) {
        return str; // Return the str so we don't convert null to undefined or vice versa
    }

    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Runs a task and calls a callback if the task takes longer than the specified timeout.
 *
 * @template TAsyncTaskReturn The return type of `asyncTask`.
 *
 * @param {() => Promise<TAsyncTaskReturn>} asyncTask The task to run.
 * @param {() => void} callback The callback to call if the task takes longer than the timeout.
 * @param {number} [timeout] The timeout in milliseconds. Default is 100ms.
 *
 * @return {Promise<TAsyncTaskReturn>} The return value from `task`.
 */
export async function runTaskWithCallback(asyncTask, callback, timeout = 100) {
    const timeoutId = setTimeout(callback, timeout);

    // No except block to allow exceptions to bubble up
    try {
        return await asyncTask();
    } finally {
        clearTimeout(timeoutId);
    }
}
